import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * Tracks deferred removal of each submenu's `[data-active]` attribute
   * after it closes, so the cleanup can be cancelled if that specific
   * submenu is reopened before its close transition finishes.
   *
   * Keyed per-submenu (not a single shared slot) because hovering across
   * several nav items in one sweep (e.g. SHOP -> JOURNAL -> COMMUNITY ->
   * ABOUT) deactivates each of them in turn. A single shared field would
   * only ever remember the most recently scheduled cleanup - scheduling
   * item N's cleanup would silently cancel item N-1's without it ever
   * running, leaving N-1 permanently marked [data-active] (and therefore
   * `content-visibility: visible`, full height, `pointer-events: auto`)
   * forever. That stuck, invisible, fully-interactive panel is what
   * produced the "white space" that swallowed hover and blocked
   * reopening the nav after scrolling past the affected item.
   * @type {Map<HTMLElement, number>}
   */
  #pendingSubmenuCleanups = new Map();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#deactivate());
    // on load, cache the max height of the submenu so you can use it in a translate
    this.#cacheMaxOverflowMenuHeight();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.#cleanupMutationObserver();
    for (const timeoutId of this.#pendingSubmenuCleanups.values()) {
      clearTimeout(timeoutId);
    }
    this.#pendingSubmenuCleanups.clear();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    this.#cacheMaxOverflowMenuHeight();
    setHeaderMenuStyle();
  }, 100);

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      // Reset the previous item's own submenu height immediately so a
      // stale non-zero value can never bleed into the next item if it's
      // reactivated before the close transition would otherwise finish.
      this.#resetSubmenuHeight(findSubmenu(previouslyActiveItem));
      // Switching directly from one nav item to another (a normal
      // left-to-right sweep across the bar) never fires a pointerleave
      // on the header-menu itself, so #deactivate() never runs for the
      // item being left - only for whichever item the pointer is on
      // when it finally leaves the whole menu. Without this call, every
      // item swept *through* kept [data-active] (and therefore
      // `content-visibility: visible`, full height, `pointer-events:
      // auto`) forever, since nothing ever scheduled its cleanup. That
      // produced multiple permanently-stuck, invisible, fully-interactive
      // panels stacked over the page - the "white space blocking the
      // nav" the user kept hitting.
      this.#scheduleSubmenuCleanup(findSubmenu(previouslyActiveItem));
      // Visibility itself is left entirely to the stylesheet
      // (`:has([aria-expanded='true'])` / `:hover`) rather than an inline
      // style write here. An inline write wins specificity over *all*
      // stylesheet rules, including the real `:hover` state - so if this
      // ran while the pointer was still genuinely over the previous
      // submenu (a fast diagonal move from one trigger into the next
      // item's submenu can do this), the inline `hidden` would fight the
      // browser's own `:hover` and the panel could vanish out from under
      // the cursor. Aria-expanded is already cleared above, which is all
      // the CSS needs to react correctly.
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const isOverflowSubmenu = !submenu && !isDefaultSlot;

    if (isOverflowSubmenu) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Cancel any pending removal of [data-active] left over from this
      // submenu's last close. If we don't, and the timeout fires after
      // we've just re-marked it active, content-visibility would flip
      // back to `auto` mid-open and discard the layout/paint state we
      // just relied on - see #deactivate for the matching deferral.
      const pendingTimeoutId = this.#pendingSubmenuCleanups.get(submenu);
      if (pendingTimeoutId !== undefined) {
        clearTimeout(pendingTimeoutId);
        this.#pendingSubmenuCleanups.delete(submenu);
      }

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.#setSubmenuHeight(submenu, isOverflowSubmenu, submenu.offsetHeight);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.#setSubmenuHeight(submenu, isOverflowSubmenu, submenuHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const item = this.#state.activeItem;
    const menu = findSubmenu(item);
    const related = event.relatedTarget;

    // Use the element the pointer/focus actually moved to (relatedTarget)
    // rather than document.activeElement. activeElement is only set during
    // keyboard navigation, so the previous focus-based guard never matched
    // for plain mouse hovering - meaning moving the cursor down from the
    // nav link into its own dropdown panel was incorrectly treated as
    // "leaving the menu" and closed it immediately.
    const isMovingWithinTrigger = related instanceof Node && item?.contains(related);
    const isMovingWithinSubmenu = related instanceof Node && menu?.contains(related);
    const isMovingToOverflowMenu =
      related instanceof Element && related.closest?.('[slot="overflow"]') != null;

    if (isMovingWithinTrigger || isMovingWithinSubmenu || isMovingToOverflowMenu) return;

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    const submenu = findSubmenu(item);
    const isOverflowSubmenu = !submenu;

    this.#setSubmenuHeight(submenu ?? this.overflowMenu, isOverflowSubmenu, 0);
    // No inline visibility write here - same reasoning as activate():
    // the stylesheet's `:has([aria-expanded='true'])` / `:hover` rules
    // own visibility exclusively. aria-expanded is cleared below, which
    // is sufficient for CSS to react.
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Defer removing [data-active] until the close transition
    // (--submenu-animation-speed) has actually finished. Removing it
    // immediately flips content-visibility back to `auto` while the
    // panel is still visually animating closed, letting the browser
    // discard its layout/paint state mid-transition. A quick hover-off/
    // hover-on then forces a full relayout+repaint from scratch before
    // the panel can reopen - slower than the very first open - and the
    // open transition starts before that repaint finishes, producing a
    // blank or partially-rendered panel that only fills in a second or
    // so later. This was the root cause of the dropdown breaking on the
    // second hover after working fine the first time.
    this.#scheduleSubmenuCleanup(submenu);
  };

  /**
   * Schedules removal of a submenu's `[data-active]` attribute once its
   * close transition (--submenu-animation-speed) has actually finished,
   * cancelling and replacing any cleanup already pending for that same
   * submenu. Shared between #deactivate() (leaving the whole menu) and
   * activate() (switching directly to a different nav item), since both
   * paths can leave a submenu's [data-active] needing to be cleared.
   * Tracked per-submenu in a Map - never as a single shared field - so
   * scheduling one submenu's cleanup can never cancel/orphan another's.
   * @param {HTMLElement | null} submenu
   */
  #scheduleSubmenuCleanup(submenu) {
    if (!submenu) return;
    const existingTimeoutId = this.#pendingSubmenuCleanups.get(submenu);
    if (existingTimeoutId !== undefined) {
      clearTimeout(existingTimeoutId);
    }
    const timeoutId = setTimeout(() => {
      delete submenu.dataset.active;
      this.#pendingSubmenuCleanups.delete(submenu);
    }, this.#getAnimationDurationMs());
    this.#pendingSubmenuCleanups.set(submenu, timeoutId);
  }

  /**
   * Reads --submenu-animation-speed so the deferred [data-active]
   * cleanup matches however long the close transition actually takes,
   * rather than a hardcoded guess.
   * @returns {number}
   */
  #getAnimationDurationMs() {
    const value = getComputedStyle(this).getPropertyValue('--submenu-animation-speed').trim();
    if (!value) return 360;
    const ms = value.endsWith('ms') ? parseFloat(value) : parseFloat(value) * 1000;
    return Number.isFinite(ms) && ms > 0 ? ms : 360;
  }

  /**
   * Writes the submenu height to the correct scope: per-element for
   * regular dropdown submenus, so a stale or zero height belonging to one
   * item can never be read by a different item mid-transition (this is
   * what previously caused a blank/broken panel to flash when hovering
   * off a menu item and back on quickly). The overflow ("more") menu has
   * no per-item element to scope to, so it keeps using the shared
   * header-level custom property, which is its only consumer.
   * @param {HTMLElement | null} submenu
   * @param {boolean} isOverflowSubmenu
   * @param {number} height
   */
  #setSubmenuHeight(submenu, isOverflowSubmenu, height) {
    if (submenu && !isOverflowSubmenu) {
      submenu.style.setProperty('--submenu-height', `${height}px`);
    } else {
      this.headerComponent?.style.setProperty('--submenu-height', `${height}px`);
    }
  }

  /**
   * Clears a previously active item's own submenu height.
   * @param {HTMLElement | null} submenu
   */
  #resetSubmenuHeight(submenu) {
    submenu?.style.setProperty('--submenu-height', '0px');
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Caches the maximum height of all submenus for consistent animations
   * Stores the value in a CSS custom property for use in transitions
   */
  #cacheMaxOverflowMenuHeight() {
    const submenus = this.querySelectorAll('[ref="submenu[]"]');
    const maxHeight = Math.max(
      ...Array.from(submenus)
        .filter((submenu) => submenu instanceof HTMLElement)
        .map((submenu) => submenu.offsetHeight)
    );
    this.headerComponent?.style.setProperty('--submenu-max-height', `${maxHeight}px`);
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
